/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {defineMessage} from 'react-intl'
import {useCategory} from '@salesforce/commerce-sdk-react'
import {useLocation, useParams, Redirect} from 'react-router-dom'
import useMultiSite from '@app/hooks/use-multi-site'
import RedirectWithStatus from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/redirect-with-status'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
// Components
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'

import ProductListType from './partials/product-list-type'
import EmptySearchResults from './partials/empty-results'
import AbovePageHeader from '@salesforce/retail-react-app/app/pages/product-list/partials/above-page-header'
import {ProductListProvider, useProductList} from './util/product-list-context'

// Custom hooks
import {useGTM} from '@app/hooks/use-gtm'
import useSiteType from '@app/hooks/use-site-type'

import {PAGE_NOT_FOUND_PDC_PAGE_ID} from '@app/constants'
export const REFINEMENT_LEGEND = defineMessage({
    defaultMessage: 'Possible filters for: {label}',
    id: 'product_list.refinements.legend'
})

export const PRODUCT_LIST_TOTAL = defineMessage({
    defaultMessage: '{total, plural, =0 {No products} one {# product} other {# products}}',
    id: 'product_list.total_products'
})

// Utils
import {categoryUrlBuilder} from '../../utils/seo-utils'

// Custom Hooks
import useCategoryUrlLifeCycle from '../../hooks/use-category-url-life-cycle'

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = (props) => {
    const {buildURLWithUTM} = useMultiSite()
    const {isOutlet} = useSiteType()
    const params = useParams()
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery

    const {
        error: categoryError,
        data: category,
        isError: isCategoryError
    } = useCategory(
        {
            parameters: {
                id: params.categoryId,
                levels: 2
            }
        },
        {
            enabled: !isSearch && !!params.categoryId
        }
    )

    const {data: urlLifeCycleData} = useCategoryUrlLifeCycle(params.categoryId)

    // Build category URL and redirect the user
    if (
        isServer &&
        urlLifeCycleData?.data?.categoryIdToRedirect &&
        urlLifeCycleData?.data?.parentCategoryTree
    ) {
        return (
            <RedirectWithStatus
                status={301}
                to={buildURLWithUTM(categoryUrlBuilder(urlLifeCycleData.data.categoryIdToRedirect))}
            />
        )
    }

    /**************** Error Handling ****************/
    if (isCategoryError) {
        const errorStatus = categoryError?.response?.status
        if (errorStatus) {
            return (
                <Redirect
                    to={buildURLWithUTM(
                        `/${PAGE_NOT_FOUND_PDC_PAGE_ID + (isOutlet ? '-outlet' : '')}`
                    )}
                />
            )
        }
    }

    return (
        <ProductListProvider productListProps={{...props, category}}>
            <ProductListContent />
        </ProductListProvider>
    )
}

const ProductListContent = () => {
    const {productListPropsRest, searchQuery, category, showNoResults, categoryType, isSearch} =
        useProductList()
    const {usePageLoadAndCookies, triggerSearchEvent} = useGTM()

    usePageLoadAndCookies(isSearch ? 'search-result' : 'category')

    useEffect(() => {
        /* GTM Integration Search Results */
        if (isSearch) {
            triggerSearchEvent(searchQuery)
        }
    }, [searchQuery])

    return (
        <Box
            className="sf-product-list-page"
            data-testid="sf-product-list-page"
            layerStyle="page"
            padding="0"
            {...productListPropsRest}
        >
            <Helmet>
                <title>{category?.pageTitle ?? searchQuery}</title>
                <meta name="description" content={category?.pageDescription ?? searchQuery} />
                <meta name="keywords" content={category?.pageKeywords} />
            </Helmet>
            {showNoResults ? (
                <EmptySearchResults searchQuery={searchQuery} category={category} />
            ) : (
                <>
                    <AbovePageHeader />

                    {/* Include PLP type */}
                    <ProductListType categoryType={categoryType} isSearch={isSearch} />
                </>
            )}
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.propTypes = {
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object
}

export default ProductList
